// The legacy build is required: the modern build ships syntax webpack 4
// cannot parse, and the legacy one supports older WebViews.
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
// pdf.js's legacy build polyfills only what its own targets lack, not these (built in since Chrome 92 and 98).
// The page has them already through app/index.js's core-js; listed here because the main-thread worker below
// depends on them.
import 'core-js/actual/array/at';
import 'core-js/actual/string/at';
import 'core-js/actual/typed-array/at';
import 'core-js/actual/structured-clone';
import { useState, useEffect, useRef } from 'react';
import Bugsnag from '@bugsnag/js';
import { nativeBuiltIns } from '@helpers/nativeBuiltIns';
import { importWorkerModule } from './workerModule';

function isFunction(value: any) {
  return typeof value === 'function';
}

// The worker is copied into www/ by the CopyPlugin entry in webpack.config.js, so it always matches the
// bundled pdfjs version. On file:// (Cordova iOS) the document's origin is "null", pdfjs treats the local file
// as cross-origin, and WKWebView blocks the resulting module import — so only there, load the version-matched
// worker from a CORS-enabled CDN like the pre-4.x code did.
// Always an absolute URL: when a Worker can't start, pdf.js import()s the same file on the page (its "fake
// worker"), and import() reads a bare 'pdf.worker.min.mjs' as a package name and fails — the preview then
// spins forever.
export const defaultWorkerSrc = (): string => (window.location.protocol === 'file:'
  ? `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`
  : new URL('pdf.worker.min.mjs', document.baseURI).href);

// pdf.js parses the PDF in a Web Worker, which gets only the browser's native built-ins: the page's core-js
// doesn't reach it. Where Array.prototype.at isn't native (Chrome < 92, Firefox < 90, Safari < 15.4 on the web) the
// worker dies while loading — "e.at is not a function", which the browser also reports on the page, to Bugsnag —
// and pdf.js falls back to running it on the page. There, go to the page directly: load the worker module here,
// where core-js filled the gaps, and hand it to pdf.js (globalThis.pdfjsWorker), which then never starts a
// Worker. Every other browser keeps the real worker. (pdfjs-dist 4.10's worker needs nothing else Chrome 92+
// lacks; after an upgrade, check its unguarded built-ins again.)
// The iOS app (file://) loads the CDN worker, which isn't transpiled and needs iOS 16.4 either way.
let loading: ?Promise<void> = null;
let attempts = 0;
let reportedLoadFailure = false;
export const workerReady = (src: string): Promise<void> => {
  if (nativeBuiltIns.arrayAt || window.pdfjsWorker) return Promise.resolve();
  if (!loading) {
    // Absolute: import() reads a bare 'pdf.worker.min.mjs' as a package name. A retry asks for its own URL: the
    // browser keeps a failed module import for its URL and fails it again at once, without fetching it
    const url = new URL(src, document.baseURI).href;
    const current = importWorkerModule(attempts ? `${url}${url.includes('?') ? '&' : '?'}retry=${attempts}` : url)
      .then((worker) => { window.pdfjsWorker = worker; });
    attempts += 1;
    loading = current;
    current.catch((error) => {
      // The next preview tries again
      if (loading === current) loading = null;
      // Once a page load: before, the dying Worker reached Bugsnag through the page; now this is how we'd know
      if (reportedLoadFailure) return;
      reportedLoadFailure = true;
      Bugsnag.notify(error, (event) => {
        event.context = 'PDFPreviewWorkerLoadFailed'; // eslint-disable-line no-param-reassign
      });
    });
  }
  return loading;
};

type HookProps = {
  canvasRef: any,
  file: string,
  onDocumentLoadSuccess?: (document: any) => void,
  onDocumentLoadFail?: () => void,
  onPageLoadSuccess?: (page: any) => void,
  onPageLoadFail?: () => void,
  onPageRenderSuccess?: (page: any) => void,
  onPageRenderFail?: () => void,
  scale?: number,
  rotate?: number,
  page?: number,
  cMapUrl?: string,
  cMapPacked?: boolean,
  workerSrc?: string,
  withCredentials?: boolean,
};

type HookReturnValues = {
  pdfDocument: any | undefined,
  pdfPage: any | undefined,
};

export const usePdf = ({
  canvasRef,
  file,
  onDocumentLoadSuccess,
  onDocumentLoadFail,
  onPageLoadSuccess,
  onPageLoadFail,
  onPageRenderSuccess,
  onPageRenderFail,
  scale = 1,
  rotate = 0,
  page = 1,
  cMapUrl,
  cMapPacked,
  workerSrc = defaultWorkerSrc(),
  withCredentials = false,
}: HookProps): HookReturnValues => {
  const [pdfDocument, setPdfDocument] = useState();
  const [pdfPage, setPdfPage] = useState();
  const renderTask = useRef(null);
  const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
  const onDocumentLoadFailRef = useRef(onDocumentLoadFail);
  const onPageLoadSuccessRef = useRef(onPageLoadSuccess);
  const onPageLoadFailRef = useRef(onPageLoadFail);
  const onPageRenderSuccessRef = useRef(onPageRenderSuccess);
  const onPageRenderFailRef = useRef(onPageRenderFail);

  // assign callbacks to refs to avoid redrawing
  useEffect(() => {
    onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
  }, [onDocumentLoadSuccess]);

  useEffect(() => {
    onDocumentLoadFailRef.current = onDocumentLoadFail;
  }, [onDocumentLoadFail]);

  useEffect(() => {
    onPageLoadSuccessRef.current = onPageLoadSuccess;
  }, [onPageLoadSuccess]);

  useEffect(() => {
    onPageLoadFailRef.current = onPageLoadFail;
  }, [onPageLoadFail]);

  useEffect(() => {
    onPageRenderSuccessRef.current = onPageRenderSuccess;
  }, [onPageRenderSuccess]);

  useEffect(() => {
    onPageRenderFailRef.current = onPageRenderFail;
  }, [onPageRenderFail]);

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
  }, [workerSrc]);

  useEffect(() => {
    const config: any = { url: file, withCredentials };
    if (cMapUrl) {
      config.cMapUrl = cMapUrl;
      config.cMapPacked = cMapPacked;
    }
    let cancelled = false;
    let loadingTask = null;
    const failed = () => {
      if (cancelled) return;
      if (isFunction(onDocumentLoadFailRef.current)) {
        onDocumentLoadFailRef.current();
      }
    };
    // One chain, so a failed worker load, a getDocument() that throws and a document that fails all end in `failed`
    workerReady(pdfjs.GlobalWorkerOptions.workerSrc)
      .then(() => {
        if (cancelled) return null;
        loadingTask = pdfjs.getDocument(config);
        return loadingTask.promise;
      })
      .then(
        loadedPdfDocument => {
          if (cancelled || !loadedPdfDocument) return;
          setPdfDocument(loadedPdfDocument);

          if (isFunction(onDocumentLoadSuccessRef.current)) {
            onDocumentLoadSuccessRef.current(loadedPdfDocument);
          }
        },
        failed,
      );
    return () => {
      // destroy() terminates the per-document worker; without it every
      // open/close of the preview leaks a worker thread and its memory.
      cancelled = true;
      if (loadingTask) loadingTask.destroy();
    };
  }, [file, withCredentials, cMapUrl, cMapPacked]);

  useEffect(() => {
    // draw a page of the pdf
    const drawPDF = (pPage: any) => {
      // Because this page's rotation option overwrites pdf default rotation value,
      // calculating page rotation option value from pdf default and this component prop rotate.
      const rotation = rotate === 0 ? pPage.rotate : pPage.rotate + rotate;
      const dpRatio = window.devicePixelRatio;
      const adjustedScale = scale * dpRatio;
      const viewport = pPage.getViewport({ scale: adjustedScale, rotation });
      const canvasEl = canvasRef.current;
      if (!canvasEl) {
        return;
      }

      const canvasContext = canvasEl.getContext('2d');
      if (!canvasContext) {
        return;
      }

      canvasEl.style.width = `${viewport.width / dpRatio}px`;
      canvasEl.style.height = `${viewport.height / dpRatio}px`;
      canvasEl.height = viewport.height;
      canvasEl.width = viewport.width;

      // if previous render isn't done yet, we cancel it
      if (renderTask.current) {
        renderTask.current.cancel();
        return;
      }

      renderTask.current = pPage.render({
        canvasContext,
        viewport,
      });

      return renderTask.current.promise.then(
        () => {
          renderTask.current = null;

          if (isFunction(onPageRenderSuccessRef.current)) {
            onPageRenderSuccessRef.current(pPage);
          }
        },
        err => {
          renderTask.current = null;

          // @ts-ignore typings are outdated
          if (err && err.name === 'RenderingCancelledException') {
            drawPDF(pPage);
          } else if (isFunction(onPageRenderFailRef.current)) {
            onPageRenderFailRef.current();
          }
        },
      );
    };

    if (pdfDocument) {
      pdfDocument.getPage(page).then(
        loadedPdfPage => {
          setPdfPage(loadedPdfPage);

          if (isFunction(onPageLoadSuccessRef.current)) {
            onPageLoadSuccessRef.current(loadedPdfPage);
          }

          drawPDF(loadedPdfPage);
        },
        () => {
          if (isFunction(onPageLoadFailRef.current)) {
            onPageLoadFailRef.current();
          }
        },
      );
    }
  }, [canvasRef, page, pdfDocument, rotate, scale]);

  return { pdfDocument, pdfPage };
};
